/* eslint-disable @typescript-eslint/naming-convention */
import { effect, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import mixpanel, { Mixpanel } from 'mixpanel-browser';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, take } from 'rxjs';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { LanguagesService } from '@shure/shared/angular/utils/i18n';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { AnalyticsAppInfo, AnalyticsEvents, AnalyticsSuperProperties } from '../models';

@Injectable()
export class AnalyticsService {
	private appInfo!: AnalyticsAppInfo;
	public mixpanel: Mixpanel;

	// setup a signal for the token claims that waits for a non-null value, and then only emits it once.
	public oktaTokenClaims = toSignal(
		this.oktaInterfaceService.$idTokenClaims.pipe(
			filter((claims) => claims !== null),
			take(1)
		)
	);

	public readonly isMixpanelInitialized = signal(false);
	public readonly isAnalyticsOptedIn = signal(false);

	private pendingOptInEvent = false;
	private logger: ILogger;

	constructor(
		logger: ILogger,
		private deviceService: DeviceDetectorService,
		private oktaInterfaceService: OktaInterfaceService,
		private languagesService: LanguagesService
	) {
		this.mixpanel = mixpanel;
		this.logger = logger.createScopedLogger('AnalyticsService');

		// Watch for authentication and send any pending events once user is authenticated
		effect(() => {
			const claims = this.oktaTokenClaims();
			if (claims?.sub) {
				if (this.pendingOptInEvent) {
					this.pendingOptInEvent = false;
					const localStorageOptState = localStorage.getItem(AnalyticsEvents.OptInState);
					this.analyticDataCollector(AnalyticsEvents.OptInState, { opted_in: localStorageOptState });
					localStorage.setItem(AnalyticsEvents.OptInState, String(localStorageOptState));
				}
			}
		});
	}

	/**
	 * Set app name and version information for analytics
	 */
	public setApplicationInfo(appInfo: AnalyticsAppInfo): void {
		this.appInfo = appInfo;
	}

	/**
	 * Set the analytic data from application
	 * @param event - String representation of event id
	 * @param data - DataObjectType representation of data from event
	 */
	public analyticDataCollector(event: string, data: object): void {
		this.submitToAnalyticsPlatform(event, data);
	}

	/**
	 * Submit the Analytic data
	 * @param event - String representation of event id
	 * @param data - DataObjectType representation of data from event
	 */
	public submitToAnalyticsPlatform(event: string, data: object): void {
		// can't track events until mix panel is initialized.
		if (!this.isMixpanelInitialized()) return;

		if (this.isAnalyticsOptedIn()) {
			const eventData = {
				...data,
				...this.getSuperProperties()
			};

			this.logger.debug('submitToAnalyticsPlatform', `${event} event: `, eventData);
			this.mixpanel.track(event, eventData);
		}
	}

	/**
	 * Initializes Mixpanel with provided project token
	 */
	public initMixPanel(application: string, applicationVersion: string, mixpanelProjectToken?: string): void {
		this.setApplicationInfo({
			application: application,
			application_version: applicationVersion
		});
		if (mixpanelProjectToken) {
			mixpanel?.init(mixpanelProjectToken, { ip: false });
			this.isMixpanelInitialized.set(true);
		}
	}

	/**
	 * Sends the sign in event
	 */
	public sendSignInEvent(): void {
		const localStorageSignIn = localStorage.getItem(AnalyticsEvents.SignIn);
		// only send if the cached value is not already signed in
		if (localStorageSignIn !== 'true') {
			this.analyticDataCollector(AnalyticsEvents.SignIn, {});
			localStorage.setItem(AnalyticsEvents.SignIn, 'true');
		}
	}

	/**
	 * Sends the opt_state event with opted_in: true
	 */
	public sendOptInEvent(): void {
		// Queue event if user isn't authenticated yet
		if (!this.oktaTokenClaims()?.sub) {
			this.pendingOptInEvent = true;
			return;
		}

		const localStorageOptState = localStorage.getItem(AnalyticsEvents.OptInState);
		// only send if the cached value is not already opted in
		if (localStorageOptState !== 'true') {
			this.analyticDataCollector(AnalyticsEvents.OptInState, { opted_in: true });
			localStorage.setItem(AnalyticsEvents.OptInState, 'true');
		}
	}

	/**
	 * Sends the opt_state event with opted_in: false
	 */
	public sendOptOutEvent(): void {
		const localStorageOptState = localStorage.getItem(AnalyticsEvents.OptInState);
		// only send if the cached value is not already opted out
		if (localStorageOptState !== 'false') {
			// temporarily set opt in to true to send the opt out event
			this.isAnalyticsOptedIn.set(true);
			this.analyticDataCollector(AnalyticsEvents.OptInState, { opted_in: false });
			localStorage.setItem(AnalyticsEvents.OptInState, 'false');
		}

		this.isAnalyticsOptedIn.set(false);
	}

	/**
	 * Gets the super or common properties of an analytics event
	 */
	private getSuperProperties(): AnalyticsSuperProperties {
		return {
			...this.appInfo,
			distinct_id: this.oktaTokenClaims()?.sub ?? 'unknown',
			session_id: this.oktaTokenClaims()?.jti ?? 'unknown',
			browser_name:
				this.deviceService.browser === 'MS-Edge-Chromium' ? 'Microsoft Edge' : this.deviceService.browser,
			browser_version: this.deviceService.browser_version,
			os_version: this.getOsVersion(this.deviceService.os),
			os_product: this.getOsProduct(this.deviceService.os),
			os_manufacturer: this.getManufacturerName(),
			timestamp: Date.now(),
			// eslint-disable-next-line dot-notation
			country: <string>this.oktaTokenClaims()?.['Custom_Country'] ?? 'unknown',
			// language service instead of locale in localStorage to fix bug on optout where locale is removed and shows empty in events
			language: this.languagesService.getPreferredCultureCode(),
			// eslint-disable-next-line dot-notation
			tenant_id: <string>this.oktaTokenClaims()?.['shure_tenant'] ?? 'unknown'
		};
	}

	/**
	 * Gets the os manufacturer name
	 */
	private getManufacturerName(): string {
		const os = this.deviceService.os;
		switch (os) {
			case 'Windows':
				return 'Microsoft';
			case 'Mac':
				return 'Apple';
			case 'Android':
				return 'Android (Google)';
			case 'iOS':
				return 'iOS (Apple)';
			default:
				return `Other operating system ${os}`;
		}
	}

	/**
	 * Gets the os product
	 */
	private getOsProduct(os: string): string {
		//for ipad we should send iPadOS
		if (os === 'iOS' && this.deviceService.deviceType === 'tablet') {
			return 'iPadOS';
		}
		return os;
	}

	/**
	 * Gets the os version
	 */
	private getOsVersion(os: string): string {
		//As we are unable to retrieve the right versions for OS other than windows, we return NA
		if (os === 'Windows') {
			const osVersion = this.deviceService.os_version;
			const formatOsVersion = osVersion?.split('-').join(' ');
			return formatOsVersion?.charAt(0)?.toUpperCase() + formatOsVersion.slice(1);
		}
		return 'NA';
	}
}
